import { compareErrorCode } from "@next-platform/client-common-core";
import { InputValidationErrorType, RateLimitingErrorType, UserErrorType } from "@next-platform/packages-ts-types";
import type { TFunction } from "i18next";

import { EmailChangeStep } from "@/pages/UpdateEmail/constants";
import type { ServerErrorResponse } from "@/types/api";

type ResolveEmailChangeErrorMessageArgs = {
  errorData: ServerErrorResponse | null | undefined;
  step: EmailChangeStep;
  t: TFunction;
};

/**
 * Turns a server error into the copy shown under the form.
 *
 * Codes are read before the step, because a case does not always belong to the step it appears on:
 * confirm re-checks uniqueness, so an address claimed by someone else during the validity window
 * surfaces on the code form even though the submitted code was correct. Deciding by step there
 * would tell the user their code was wrong.
 *
 * INVALID_REQUEST stays step-dependent. It covers schema rejections, and its `message` cannot be
 * rendered: the server prefixes every detail with "The user input is invalid or missing any
 * required field.", which would surface as a stray technical sentence.
 */
const resolveEmailChangeErrorMessage = ({ errorData, step, t }: ResolveEmailChangeErrorMessageArgs): string | null => {
  const { errorCode, error, message } = errorData ?? {};

  if (!error && !message) {
    return null;
  }

  if (compareErrorCode(errorCode, RateLimitingErrorType.RATE_LIMIT_BREACHED)) {
    return t("user:updateEmail.request.error.rateLimited");
  }

  if (compareErrorCode(errorCode, UserErrorType.EMAIL_DUPLICATED)) {
    return t("user:updateEmail.request.error.duplicated");
  }

  if (compareErrorCode(errorCode, UserErrorType.EMAIL_UNCHANGED)) {
    return t("user:updateEmail.request.error.unchanged");
  }

  if (compareErrorCode(errorCode, UserErrorType.EMAIL_VERIFICATION_CODE_INVALID)) {
    return t("user:updateEmail.verify.error.codeInvalid");
  }

  // The server destroys the pending change once it expires or once the attempts run out, so both
  // read back as "nothing pending" and the only way forward is a new code.
  if (compareErrorCode(errorCode, UserErrorType.EMAIL_VERIFICATION_NOT_FOUND)) {
    return t("user:updateEmail.verify.expired");
  }

  if (compareErrorCode(errorCode, UserErrorType.EMAIL_VERIFICATION_TOO_MANY_ATTEMPTS)) {
    return t("user:updateEmail.verify.error.tooManyAttempts");
  }

  if (compareErrorCode(errorCode, InputValidationErrorType.INVALID_REQUEST)) {
    return step === EmailChangeStep.CONFIRM ? t("user:updateEmail.verify.error.generic") : t("user:updateEmail.request.error.unavailable");
  }

  return t("error:fallbackError");
};

export default resolveEmailChangeErrorMessage;
